import React from 'react'
import { Helmet } from '../../lib/react-helmet-async'
import { ENTITY } from '../../data/entityData'

export const SchemaMarkup = ({
  pageTitle,
  pageDescription,
  pageUrl,
  canonicalUrl,
  faqItems,
  articleData,
}) => {

  const fullUrl = pageUrl
    ? `${ENTITY.url}${pageUrl}`
    : ENTITY.url

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": [ENTITY.schemaType, "LocalBusiness"],
    "@id": `${ENTITY.url}/#identity`,
    "name": ENTITY.name,
    "legalName": ENTITY.legalName,
    "url": ENTITY.url,
    "logo": {
      "@type": "ImageObject",
      "url": ENTITY.logo
    },
    "image": ENTITY.logo,
    "telephone": ENTITY.phone,
    "email": ENTITY.email,
    "foundingDate": ENTITY.founded,
    "founder": {
      "@type": "Person",
      "name": ENTITY.founder,
      "jobTitle": ENTITY.founderProfile.jobTitle,
      "knowsAbout": ENTITY.founderProfile.knowsAbout,
      "knowsLanguage": ENTITY.founderProfile.languages
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": ENTITY.address.streetAddress,
      "addressLocality": ENTITY.address.addressLocality,
      "addressRegion": ENTITY.address.addressRegion,
      "postalCode": ENTITY.address.postalCode,
      "addressCountry": ENTITY.address.addressCountry
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": ENTITY.geo.latitude,
      "longitude": ENTITY.geo.longitude
    },
    "openingHoursSpecification": ENTITY.hours.map(h => {
      const [day, time] = h.split(' ')
      const [opens, closes] = time.split('-')
      const dayMap = {
        Mo: 'Monday', Tu: 'Tuesday',
        We: 'Wednesday', Th: 'Thursday',
        Fr: 'Friday', Sa: 'Saturday', Su: 'Sunday'
      }
      return {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek":
          `https://schema.org/${dayMap[day]}`,
        "opens": opens,
        "closes": closes
      }
    }),
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": ENTITY.rating.value,
      "reviewCount": ENTITY.rating.count,
      "bestRating": "5",
      "worstRating": "1"
    },
    "priceRange": ENTITY.priceRange,
    "currenciesAccepted": ENTITY.currencies,
    "paymentAccepted": ENTITY.paymentMethods,
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `${ENTITY.name} Services`,
      "itemListElement": ENTITY.services.map(s => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "@id": `${ENTITY.url}/#${s.id}`,
          "name": s.name,
          "description": s.description,
          "areaServed": s.areaServed,
          "provider": {
            "@id": `${ENTITY.url}/#identity`
          }
        }
      }))
    },
    "sameAs": ENTITY.sameAs,
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": ENTITY.phone,
      "contactType": "customer service",
      "availableLanguage":
        ENTITY.founderProfile.languages
    },
    "description": pageDescription || "",
    "knowsAbout": ENTITY.intentPhrases
  }

  const faqSchema = faqItems?.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null

  const articleSchema = articleData ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": articleData.headline,
    "description": articleData.description,
    "datePublished": articleData.datePublished,
    "dateModified": articleData.dateModified,
    "image": articleData.image || ENTITY.logo,
    "author": {
      "@type": "Person",
      "name": ENTITY.founder,
      "url": ENTITY.url
    },
    "publisher": {
      "@type": "Organization",
      "name": ENTITY.name,
      "logo": {
        "@type": "ImageObject",
        "url": ENTITY.logo
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": fullUrl
    }
  } : null

  return (
    <Helmet>
      <title>{pageTitle || ENTITY.name}</title>
      <meta name="description"
        content={pageDescription || ''} />
      <link rel="canonical"
        href={canonicalUrl || fullUrl} />
      <meta property="og:title"
        content={pageTitle || ENTITY.name} />
      <meta property="og:description"
        content={pageDescription || ''} />
      <meta property="og:url"
        content={canonicalUrl || fullUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="en_ET" />
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema, null, 0)}
      </script>
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema, null, 0)}
        </script>
      )}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema, null, 0)}
        </script>
      )}
    </Helmet>
  )
}
